import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Box,
    Chip,
    Divider,
    FormControl,
    Icon,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { ChangeEvent, useContext } from "react";
import { useSelector } from "react-redux";
import {
    AccessDto,
    AccessProjectType,
    AccountSubscriptionType,
    FolderDto,
    ProjectFilterInput,
    ProjectSearchType,
} from "../../../api/psc";
import { AppDatePicker, MultiSelect } from "../../../components/exports";
import RangeSelect from "../../../components/shared/RangeSelect";
import {
    floorRange,
    groundAreaRange,
    residentialAreaRange,
    subFloorRange,
    unitRange,
} from "../../../constants/ProjectConstants";
import { DialogContext } from "../../../contexts/DialogueContext";
import { RootState } from "../../../slices/Store";
import { projectFilterIconStyle } from "../../../styles/ProjectDefaultStyles";
import SelectFolderItem from "../../project-detail/Shared/SelectFolderItem";
import ProjectAccessCountAvailabilityBox from "./ProjectAccessCountAvailabilityBox";

export enum ProjectType {
    Standard = "Standard",
    Enhanced = "Enhanced",
    Ultimate = "Ultimate",
}

export const FilterProjectTypes = [
    {
        id: AccountSubscriptionType.STANDARD,
        type: AccountSubscriptionType.STANDARD,
        title: "بنیان",
    },
    {
        id: AccountSubscriptionType.ENHANCED,
        type: AccountSubscriptionType.ENHANCED,
        title: "رویان",
    },
    {
        id: AccountSubscriptionType.ULTIMATE,
        type: AccountSubscriptionType.ULTIMATE,
        title: "تابان",
    },
    {
        id: AccountSubscriptionType.ULTIMATE_PLUS,
        type: AccountSubscriptionType.ULTIMATE_PLUS,
        title: "تابان پلاس",
    },
];

export default function ProjectFilter(props: {
    reloadCallback: () => void;
    isLoading: boolean;
    filter: ProjectFilterInput;
    onFilterChange: (filter: ProjectFilterInput) => void;
    clearFilters: () => void;
    hasAnyFilter: boolean;
}) {
    const sharedState = useSelector((state: RootState) => state.shared);
    const { closeDialog } = useContext(DialogContext);

    const applyFilter = (e) => {
        e.preventDefault();
        props.reloadCallback();
        closeDialog();
    };

    const clearFilters = () => {
        props.clearFilters();
    };

    const handleSelectedAccessChange = (access: AccessDto) => {
        const filter = {
            ...props.filter,
            targetAccessId: access.id,
            cityIds: [],
            regionIds: [],
            phaseIds: [],
            subRegionIds: [],
            requirementSubCategoryIds: [],
        };
        props.onFilterChange(filter);
    };

    const handleProjectTypesChange = (ev: AccountSubscriptionType[]) => {
        const filter = {
            ...props.filter,
            subscriptionTypes: ev,
        };
        props.onFilterChange(filter);
    };

    const handlePhaseChange = (values: number[]) => {
        const filter = { ...props.filter, phaseIds: values };
        props.onFilterChange(filter);
    };

    const handleCiyChange = (values: number[]) => {
        const filter = {
            ...props.filter,
            cityIds: values,
            regionIds: [],
            subRegionIds: [],
        };
        props.onFilterChange(filter);
    };

    const handleRegionChange = (values: number[]) => {
        const filter = { ...props.filter, regionIds: values, subRegionIds: [] };
        props.onFilterChange(filter);
    };

    const handleSubRegionChange = (values: number[]) => {
        const filter = { ...props.filter, subRegionIds: values };
        props.onFilterChange(filter);
    };

    const handleStructureTypeChange = (values: number[]) => {
        const filter = { ...props.filter, structureTypeIds: values };
        props.onFilterChange(filter);
    };

    const handleUsageTypeChange = (values: number[]) => {
        const filter = { ...props.filter, usageTypesIds: values };
        props.onFilterChange(filter);
    };

    const handleLastUpdateUpdateChange = (event: SelectChangeEvent<string>) => {
        let value = null;

        switch (event.target.value) {
            case "1":
                value = true;
                break;
            case "0":
                value = false;
                break;
            case "-1":
                value = null;
                break;
        }

        const filter = { ...props.filter, lastPhaseUpdateDateType: value };
        props.onFilterChange(filter);
    };

    const handleTermChange = (
        elem: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const filter = { ...props.filter, term: elem.target.value };
        props.onFilterChange(filter);
    };

    const handleFloorChange = (from?: number, to?: number) => {
        const filter = { ...props.filter, floorFrom: from, floorTo: to };
        props.onFilterChange(filter);
    };

    const handleUnitChange = (from?: number, to?: number) => {
        const filter = { ...props.filter, unitFrom: from, unitTo: to };
        props.onFilterChange(filter);
    };

    const handleSubFloorChange = (from?: number, to?: number) => {
        const filter = { ...props.filter, subFloorFrom: from, subFloorTo: to };
        props.onFilterChange(filter);
    };

    const handleGroundAreaChange = (from?: number, to?: number) => {
        const filter = {
            ...props.filter,
            groundAreaFrom: from,
            groundAreaTo: to,
        };
        props.onFilterChange(filter);
    };

    const handleResidentialAreaChange = (from?: number, to?: number) => {
        const filter = {
            ...props.filter,
            residentialAreaFrom: from,
            residentialAreaTo: to,
        };
        props.onFilterChange(filter);
    };

    const handleProjectsWithConstructor = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const checked = event.target.checked;
        const filter = { ...props.filter, onlyWithConstructor: checked };
        props.onFilterChange(filter);
    };

    const handleUpdateDateFromChange = (date: string) => {
        const filter = { ...props.filter, updateDateFrom: date };
        props.onFilterChange(filter);
    };

    const handleUpdateDateToChange = (date: string) => {
        const filter = { ...props.filter, updateDateTo: date };
        props.onFilterChange(filter);
    };

    const handleVisitDateFromChange = (date: string) => {
        const filter = { ...props.filter, visitDateFrom: date };
        props.onFilterChange(filter);
    };

    const handleVisitDateToChange = (date: string) => {
        const filter = { ...props.filter, visitDateTo: date };
        props.onFilterChange(filter);
    };

    const handleFolderToggle = (folder: FolderDto) => {
        const updatedFolderIds = [...props.filter.folderIds];
        const isFolderAdded = updatedFolderIds.includes(folder.id);

        if (isFolderAdded) {
            updatedFolderIds.splice(updatedFolderIds.indexOf(folder.id), 1);
        } else {
            updatedFolderIds.push(folder.id);
        }

        const filter = { ...props.filter, folderIds: updatedFolderIds };

        props.onFilterChange(filter);
    };

    const handleShowHiddenProject = (event: ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const filter = { ...props.filter, onlyHiddenProjects: checked };
        props.onFilterChange(filter);
    };

    const selectedAccess = sharedState.accessList.find(
        (a) => a.id === props.filter.targetAccessId
    );

    const isAllProjectsSelected = props.filter.type === ProjectSearchType.ALL;

    const availableCities = sharedState.baseInfo.cities.filter(
        (c) =>
            isAllProjectsSelected ||
            selectedAccess.cityIds.length === 0 ||
            selectedAccess.cityIds.includes(c.id)
    );

    const availablePhases = sharedState.baseInfo.phaseList.filter(
        (p) =>
            isAllProjectsSelected ||
            selectedAccess.phaseIds.length === 0 ||
            selectedAccess.phaseIds.includes(p.id)
    );

    const availableRegions = availableCities
        .filter(
            (c) =>
                isAllProjectsSelected ||
                availableCities.length === 1 ||
                props.filter.cityIds.includes(c.id)
        )
        .map((c) => c.regions)
        .reduce((r1, r2) => r1?.concat(r2) ?? [], [])
        .filter(
            (r) =>
                isAllProjectsSelected ||
                selectedAccess.regionIds.length === 0 ||
                selectedAccess.regionIds.includes(r.id)
        );

    const availableSubRegions = availableRegions
        .filter(
            (r) =>
                props.filter.regionIds.length === 0 ||
                props.filter.regionIds.includes(r.id)
        )
        .map((r) => r.subregions)
        .reduce((r1, r2) => r1?.concat(r2) ?? [], []);

    const getPhaseTypeAsNumber = (): string | null => {
        switch (props.filter.lastPhaseUpdateDateType) {
            case true:
                return "1";
            case false:
                return "0";
            case null:
                return "-1";
        }
    };

    return (
        <form onSubmit={applyFilter}>
            <Stack
                spacing={2}
                sx={{ p: 2, minWidth: 330 }}
            >
                <Stack
                    spacing={1}
                    direction="row"
                    alignItems="end"
                >
                    <Typography sx={{ flexGrow: 1 }}>فیلتر پروژه ها</Typography>

                    <LoadingButton
                        type="submit"
                        endIcon={<Icon>filter_alt</Icon>}
                        loading={props.isLoading}
                        variant="contained"
                    >
                        اعمال فیلتر
                    </LoadingButton>

                    <IconButton
                        onClick={closeDialog}
                        sx={{ display: { md: "none", sm: "block" } }}
                    >
                        <Icon>close</Icon>
                    </IconButton>
                </Stack>

                <Divider />

                {!isAllProjectsSelected && (
                    <FormControl fullWidth>
                        <InputLabel>نوع دسترسی</InputLabel>
                        <Select
                            label="نوع دسترسی"
                            value={selectedAccess.id}
                        >
                            {sharedState.accessList
                                .filter(
                                    (a) =>
                                        a.projectType ===
                                        AccessProjectType.NORMAL
                                )
                                .map((a) => {
                                    return (
                                        <MenuItem
                                            key={a.id}
                                            onClick={() =>
                                                handleSelectedAccessChange(a)
                                            }
                                            value={a.id}
                                        >
                                            {a.title}
                                        </MenuItem>
                                    );
                                })}
                        </Select>
                    </FormControl>
                )}

                {!isAllProjectsSelected && selectedAccess?.viewCounter && (
                    <ProjectAccessCountAvailabilityBox
                        access={selectedAccess}
                    />
                )}

                {isAllProjectsSelected && (
                    <Alert severity="warning">
                        شما در حال مشاهده همه پروژه ها بدون محدودیت دسترسی
                        میباشید، ممکن است برخی از پروژه ها قابل دسترس نباشند.
                    </Alert>
                )}

                {props.filter.type === ProjectSearchType.ALL && (
                    <MultiSelect
                        label={"انتخاب نوع اشتراک"}
                        values={FilterProjectTypes}
                        selectedIds={props.filter.subscriptionTypes}
                        onValueChange={handleProjectTypesChange}
                    />
                )}

                <Divider>
                    <Typography color="text.secondary">
                        فیلتر های اصلی
                    </Typography>
                </Divider>

                <MultiSelect
                    label="مرحله"
                    values={availablePhases}
                    selectedIds={props.filter.phaseIds}
                    onValueChange={handlePhaseChange}
                />

                {availableCities.length > 1 && (
                    <MultiSelect
                        label="شهر"
                        values={availableCities}
                        selectedIds={props.filter.cityIds}
                        onValueChange={handleCiyChange}
                    />
                )}

                {props.filter.cityIds.length <= 1 &&
                    availableRegions.length > 0 && (
                        <MultiSelect
                            label="منطقه"
                            values={availableRegions}
                            selectedIds={props.filter.regionIds}
                            onValueChange={handleRegionChange}
                        />
                    )}

                {props.filter.regionIds.length > 0 &&
                    availableSubRegions.length > 0 && (
                        <MultiSelect
                            label="محله"
                            values={availableSubRegions}
                            selectedIds={props.filter.subRegionIds}
                            onValueChange={handleSubRegionChange}
                        />
                    )}

                <TextField
                    fullWidth
                    value={props.filter.term ?? ""}
                    onChange={handleTermChange}
                    label="جستجو"
                    placeholder={"جستجو یا شناسه، موبایل، آدرس یا نام سازنده"}
                    variant="outlined"
                />

                {props.filter.type === ProjectSearchType.VISITED && (
                    <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="space-between"
                    >
                        <AppDatePicker
                            label="تاریخ مشاهده از"
                            onChange={handleVisitDateFromChange}
                            initialValue={props.filter.visitDateFrom}
                        />
                        <AppDatePicker
                            label="تاریخ مشاهده تا"
                            onChange={handleVisitDateToChange}
                            initialValue={props.filter.visitDateTo}
                        />
                    </Stack>
                )}

                {props.filter.type !== ProjectSearchType.ALL && (
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            justifyContent="space-between"
                        >
                            <Box sx={projectFilterIconStyle}>
                                <Icon color="secondary">person</Icon>
                            </Box>
                            <InputLabel sx={{ flexGrow: 1 }}>
                                پروژه های حاوی سازنده
                            </InputLabel>
                            <Switch
                                color="secondary"
                                checked={props.filter.onlyWithConstructor}
                                onChange={handleProjectsWithConstructor}
                            />
                        </Stack>

                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            justifyContent="space-between"
                        >
                            <Box sx={projectFilterIconStyle}>
                                <Icon color="warning">delete</Icon>
                            </Box>
                            <InputLabel sx={{ flexGrow: 1 }}>
                                پروژه های حذف شده
                            </InputLabel>
                            <Switch
                                color="warning"
                                checked={props.filter.onlyHiddenProjects}
                                onChange={handleShowHiddenProject}
                            />
                        </Stack>
                    </>
                )}

                {sharedState.folders.length > 0 && (
                    <Stack spacing={2}>
                        <Divider>
                            <Typography color="text.secondary">
                                پوشه ها
                            </Typography>
                        </Divider>

                        {sharedState.folders.map((f) => (
                            <SelectFolderItem
                                key={f.id}
                                folder={f}
                                onToggle={handleFolderToggle}
                                checked={props.filter.folderIds.includes(f.id)}
                            />
                        ))}
                    </Stack>
                )}

                <Divider>
                    <Typography color="text.secondary">
                        فیلتر های پیشرفته
                    </Typography>
                </Divider>

                <MultiSelect
                    label="نوع سازه"
                    values={sharedState.baseInfo.structureTypes}
                    selectedIds={props.filter.structureTypeIds}
                    onValueChange={handleStructureTypeChange}
                />

                <MultiSelect
                    label="نوع کاربری"
                    values={sharedState.baseInfo.usageTypes}
                    selectedIds={props.filter.usageTypesIds}
                    onValueChange={handleUsageTypeChange}
                />

                <FormControl fullWidth>
                    <InputLabel>تاریخ آخرین پیشرفت مرحله</InputLabel>
                    <Select
                        value={getPhaseTypeAsNumber()}
                        label="تاریخ آخرین پیشرفت مرحله"
                        onChange={handleLastUpdateUpdateChange}
                    >
                        <MenuItem value={"-1"}>همه</MenuItem>
                        <MenuItem value={"1"}>کمتر از یک سال</MenuItem>
                        <MenuItem value={"0"}>بیش از یک سال</MenuItem>
                    </Select>
                </FormControl>

                <Stack
                    spacing={2}
                    sx={{ pb: 2, pt: 2 }}
                >
                    <RangeSelect
                        label="تعداد طبقه"
                        from={props.filter.floorFrom}
                        to={props.filter.floorTo}
                        onValueChange={handleFloorChange}
                        valueProvider={floorRange}
                    />

                    <RangeSelect
                        label="تعداد واحد"
                        from={props.filter.unitFrom}
                        to={props.filter.unitTo}
                        onValueChange={handleUnitChange}
                        valueProvider={unitRange}
                    />

                    <RangeSelect
                        label="طبقات منفی"
                        from={props.filter.subFloorFrom}
                        to={props.filter.subFloorTo}
                        onValueChange={handleSubFloorChange}
                        valueProvider={subFloorRange}
                    />

                    <RangeSelect
                        label="متراژ زمین"
                        from={props.filter.groundAreaFrom}
                        to={props.filter.groundAreaTo}
                        onValueChange={handleGroundAreaChange}
                        valueProvider={groundAreaRange}
                    />

                    <RangeSelect
                        label="زیربنای مفید"
                        from={props.filter.residentialAreaFrom}
                        to={props.filter.residentialAreaTo}
                        onValueChange={handleResidentialAreaChange}
                        valueProvider={residentialAreaRange}
                    />
                </Stack>

                {/* update date */}
                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <AppDatePicker
                        label="به روز رسانی از"
                        onChange={handleUpdateDateFromChange}
                        initialValue={props.filter.updateDateFrom}
                    />

                    <AppDatePicker
                        label="به روز رسانی تا"
                        onChange={handleUpdateDateToChange}
                        initialValue={props.filter.updateDateTo}
                    />
                </Stack>

                {/* reset filters */}
                {props.hasAnyFilter && (
                    <Stack
                        direction="column"
                        spacing={2}
                    >
                        <Divider />

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Chip
                                label="حذف تمامی فیلتر ها"
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={clearFilters}
                            />
                        </Box>
                    </Stack>
                )}
            </Stack>
        </form>
    );
}
